import React from 'react';
import { Calendar, ArrowUpCircle, TrendingUp, ShoppingCart, Package, Clock } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar } from 'recharts';

function AnalyticsTab({
  analytics, selectedYear, setSelectedYear, selectedMonth, setSelectedMonth, handleExportExcel
}) {
  if (!analytics) return null;

  const months = ["Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun", "Iyul", "Avgust", "Sentabr", "Oktyabr", "Noyabr", "Dekabr"];

  return (
    <div className="space-y-10 animate-in">
      <div className="flex gap-4 bg-slate-900 p-6 rounded-[30px] border border-blue-500/10 shadow-2xl items-center">
        <Calendar className="text-blue-400" size={24} />
        <span className="font-black text-slate-400 uppercase text-xs tracking-widest">Davrni tanlang:</span>
        <select
          className="bg-slate-950 border border-white/5 rounded-xl p-3 font-bold outline-none focus:ring-2 focus:ring-blue-500/20 text-white"
          value={selectedYear}
          onChange={e => setSelectedYear(e.target.value)}
        >
          {[2026, 2027, 2028].map(y => <option key={y} value={y}>{y}-yil</option>)}
        </select>
        <select
          className="bg-slate-950 border border-white/5 rounded-xl p-3 font-bold outline-none focus:ring-2 focus:ring-blue-500/20 text-white"
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
        >
          {["Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun", "Iyul", "Avgust", "Sentabr", "Oktyabr", "Noyabr", "Dekabr"].map((m, i) => (
            <option key={i+1} value={i+1}>{m}</option>
          ))}
        </select>
        <button onClick={handleExportExcel} className="ml-auto bg-emerald-600 text-white px-6 py-3 rounded-xl font-black flex items-center gap-2 hover:bg-emerald-700 transition shadow-[0_0_20px_rgba(16,185,129,0.3)]">
          <ArrowUpCircle size={20}/> EXCELGA EKSPORT
        </button>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="bg-slate-900 p-8 rounded-[40px] border border-white/5 shadow-2xl">
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-4">Savdo Soni ({months[selectedMonth-1]} {selectedYear})</p>
          <div className="flex items-end gap-3">
            <div className="text-4xl font-black text-white">{Number(analytics.current?.sales_count || 0)}</div>
            <div className="text-[10px] text-slate-500 font-bold mb-2">ta mahsulot</div>
          </div>
        </div>
        <div className="bg-slate-900 p-8 rounded-[40px] border border-white/5 shadow-2xl">
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-4">Tushum ({months[selectedMonth-1]} {selectedYear})</p>
          <div className="text-4xl font-black text-blue-400 drop-shadow-[0_0_8px_currentColor]">{Math.round(analytics.current?.total_revenue || 0).toLocaleString()} <span className="text-sm">s</span></div>
        </div>
        <div className="bg-slate-900 p-8 rounded-[40px] border border-emerald-500/20 bg-emerald-500/5 shadow-2xl">
          <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-4">Foyda ({months[selectedMonth-1]} {selectedYear})</p>
          <div className="text-4xl font-black text-emerald-400 drop-shadow-[0_0_8px_currentColor]">{Math.round(analytics.current?.net_profit || 0).toLocaleString()} <span className="text-sm">s</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div className="bg-slate-900 p-10 rounded-[45px] border border-white/5 shadow-2xl">
          <h3 className="text-xl font-black mb-10 flex items-center gap-3 text-white"><TrendingUp className="text-blue-400"/> Oylik Foyda Trendi</h3>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.trend}>
                <defs>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12, fontWeight: 'bold'}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12, fontWeight: 'bold'}} />
                <Tooltip
                  contentStyle={{backgroundColor: '#0f172a', borderRadius: '20px', border: '1px solid rgba(59,130,246,0.2)', boxShadow: '0 0 30px rgba(0,0,0,0.5)', fontWeight: 'bold', color: '#fff'}}
                  itemStyle={{color: '#60a5fa'}}
                />
                <Area type="monotone" dataKey="profit" name="Foyda (so'm)" stroke="#60a5fa" strokeWidth={4} fillOpacity={1} fill="url(#colorProfit)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 p-10 rounded-[45px] border border-white/5 shadow-2xl">
          <h3 className="text-xl font-black mb-10 flex items-center gap-3 text-white"><ShoppingCart className="text-indigo-400"/> Oylik Savdo Hajmi</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12, fontWeight: 'bold'}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12, fontWeight: 'bold'}} />
                <Tooltip
                  contentStyle={{backgroundColor: '#0f172a', borderRadius: '20px', border: '1px solid rgba(59,130,246,0.2)', boxShadow: '0 0 30px rgba(0,0,0,0.5)', fontWeight: 'bold', color: '#fff'}}
                />
                <Bar dataKey="sales" name="Sotuv soni" fill="#818cf8" radius={[10, 10, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div className="bg-slate-900 p-10 rounded-[45px] border border-white/5 shadow-2xl">
            <h3 className="text-xl font-black mb-10 flex items-center gap-3 text-white"><Package className="text-emerald-400"/> Top 5 Mahsulot</h3>
            <div className="space-y-6">
              {analytics.topProducts?.map((p, idx) => (
                <div key={idx} className="flex justify-between items-center p-5 bg-white/5 rounded-2xl">
                  <div>
                    <p className="font-bold text-white">{p.name}</p>
                    <p className="text-xs text-slate-500">{Number(p.count)} dona sotilgan</p>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-emerald-400">+{Math.round(p.total_profit).toLocaleString()} s</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-900 p-10 rounded-[45px] border border-white/5 shadow-2xl">
            <h3 className="text-xl font-black mb-10 flex items-center gap-3 text-white"><Clock className="text-amber-400"/> Savdo Vaqtlari (24s)</h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.hourlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
                  <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 10}} />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{backgroundColor: '#0f172a', borderRadius: '15px', border: 'none', color: '#fff'}}
                  />
                  <Bar dataKey="sales" name="Sotuvlar" fill="#f59e0b" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsTab;
